import { useTranslation } from 'next-i18next'
import React from 'react'
import { OnDemandVolumeEstimate, StorageTime } from 'src/types/graphql'
import { numberWithCommas } from 'src/utils'
import { DEFAULT_PER_SQM_RATES, OnDemandCityConfig } from 'src/utils/onDemand'

import {
  DurationTierKey,
  getRateForDuration,
  getTierDiscountPct,
  storageTimeToTier,
  volumeToEstimatedM2
} from './helpers'

interface IProps {
  volume: OnDemandVolumeEstimate | null | undefined
  storageDuration: StorageTime | null | undefined
  perSqmRates: OnDemandCityConfig['perSqmRates'] | undefined
}

const TIER_LABEL_KEY: Record<
  DurationTierKey,
  'TIER_LABEL_MONTH' | 'TIER_LABEL_6' | 'TIER_LABEL_12'
> = {
  monthToMonth: 'TIER_LABEL_MONTH',
  sixMonths: 'TIER_LABEL_6',
  twelveMonths: 'TIER_LABEL_12'
}

const PriceReveal = ({ volume, storageDuration, perSqmRates }: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')

  const estimatedM2 = volumeToEstimatedM2(volume)
  const rate = getRateForDuration(storageDuration, perSqmRates)
  const tier = storageTimeToTier(storageDuration)

  const ready = estimatedM2 !== null && rate !== null && tier !== null
  const total = ready ? estimatedM2! * rate! : null

  const monthToMonthRate =
    perSqmRates?.monthToMonth ?? DEFAULT_PER_SQM_RATES.monthToMonth
  const isLongTerm = tier === 'sixMonths' || tier === 'twelveMonths'
  const isMonthToMonth = tier === 'monthToMonth'

  const savingsPerMonth =
    ready && isLongTerm ? (monthToMonthRate - rate!) * estimatedM2! : null

  const twelveMonthRate =
    perSqmRates?.twelveMonths ?? DEFAULT_PER_SQM_RATES.twelveMonths
  const nudgeTotal =
    ready && isMonthToMonth && estimatedM2
      ? estimatedM2 * twelveMonthRate
      : null

  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-5 flex flex-col gap-1 rounded-lg border border-blue-500/20 bg-blue-100/40 px-4 py-3"
    >
      <span className="font-display text-[10px] tracking-[0.18em] text-blue-500 uppercase md:text-[11px]">
        {ready
          ? t(`WIZARD.PRICE_REVEAL.${TIER_LABEL_KEY[tier!]}`)
          : t('WIZARD.PRICE_REVEAL.EYEBROW')}
      </span>
      {ready ? (
        <>
          <span className="font-display text-[18px] leading-tight font-medium tracking-[-0.01em] text-ink-700 italic md:text-[20px]">
            {t('WIZARD.PRICE_REVEAL.FORMULA', {
              rate: rate,
              m2: estimatedM2,
              total: numberWithCommas(total!)
            })}
          </span>

          {isMonthToMonth && nudgeTotal !== null && (
            <span className="mt-1 text-[12px] leading-snug text-blue-500">
              {t('WIZARD.PRICE_REVEAL.SAVINGS_NUDGE', {
                total: numberWithCommas(nudgeTotal),
                pct: getTierDiscountPct(
                  perSqmRates ?? DEFAULT_PER_SQM_RATES,
                  'twelveMonths'
                )
              })}
            </span>
          )}

          {isLongTerm && savingsPerMonth !== null && savingsPerMonth > 0 && (
            <span className="mt-1 text-[12px] leading-snug text-green-700">
              {`✓ ${t('WIZARD.PRICE_REVEAL.SAVINGS_CONFIRM', {
                savings: numberWithCommas(savingsPerMonth)
              })}`}
            </span>
          )}
        </>
      ) : (
        <span className="text-[13px] leading-relaxed text-ink-200">
          {t('WIZARD.PRICE_REVEAL.UNSURE')}
        </span>
      )}
    </div>
  )
}

export default React.memo(PriceReveal)
